/**
 * Shared Ask AI wiring — transport + navigation actions for the assistant.
 */
import * as React from "react";
import { createJsonChatTransport, type AssistantAction } from "@authe/ai-assistant";
import { useSession } from "@authe/ui-core/session";
import { useNavigate } from "@tanstack/react-router";
import { appConfig, APPLICATION_SLUG } from "@/config";

export const ASK_AI_SUGGESTIONS = [
  "Show high-risk transactions in the last 24 hours",
  "Which entities are bridging funds across chains?",
  "Summarize activity for this tenant this week",
] as const;

export function useAskAiTransport() {
  const { getAccessToken } = useSession();

  return React.useMemo(
    () =>
      createJsonChatTransport({
        url: `${appConfig.apiUrl}/ai/chat`,
        credentials: "omit",
        headers: async (): Promise<Record<string, string>> => {
          const token = await getAccessToken();
          const h: Record<string, string> = { "Source-Platform": APPLICATION_SLUG };
          if (token) h.Authorization = `Bearer ${token}`;
          return h;
        },
      }),
    [getAccessToken],
  );
}

export function useAskAiActionHandler() {
  const navigate = useNavigate();

  return React.useCallback(
    (action: AssistantAction) => {
      const a = action as { type?: string; to?: unknown };
      if (a.type === "navigate" && typeof a.to === "string") {
        navigate({ to: a.to });
      }
    },
    [navigate],
  );
}
